import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Users, Plus, ListChecks, ShoppingBag, Loader2, Star, X, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * P2P Marketplace — lists active ads filtered by user's country.
 * URL query: ?source=outside|game (controls which wallet trades route to)
 */
const P2PMarketplacePage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const api = authAxios();

  // Source defaults to outside (Dashboard P2P -> deposited_usdt)
  const params = new URLSearchParams(location.search);
  const sourceParam = params.get("source") === "game" ? "game" : "outside";

  const [status, setStatus] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Buy order modal
  const [selectedAd, setSelectedAd] = useState(null);
  const [fiatAmount, setFiatAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/p2p/me/status");
      setStatus(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/ads?side=sell");
      setAds(r.data.ads || []);
    } catch (e) {
      if (!e.isAuthExpiry && e.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Failed to load ads");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    fetchAds();
    // eslint-disable-next-line
  }, []);

  const openBuyModal = (ad) => {
    setSelectedAd(ad);
    setFiatAmount("");
  };

  const closeBuyModal = () => {
    setSelectedAd(null);
    setFiatAmount("");
  };

  const confirmBuy = async () => {
    const fiat = parseFloat(fiatAmount);
    if (!fiat || fiat <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (fiat < selectedAd.min_limit_fiat || fiat > selectedAd.max_limit_fiat) {
      toast.error(`Amount must be between ${selectedAd.min_limit_fiat} and ${selectedAd.max_limit_fiat} ${selectedAd.fiat}`);
      return;
    }
    const usdtAmount = +(fiat / selectedAd.price_per_usdt).toFixed(2);
    if (usdtAmount > selectedAd.available_amount_usdt) {
      toast.error(`Only ${selectedAd.available_amount_usdt.toFixed(2)} USDT available`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/p2p/trades", {
        ad_id: selectedAd.id,
        amount_usdt: usdtAmount,
        source: sourceParam,
      });
      const tradeId = r.data.trade_id;
      const t = r.data.trade;
      closeBuyModal();
      if (t.status === "pending_kyc") {
        toast.info("KYC required — please complete verification");
        navigate(`/p2p/kyc/${tradeId}`);
      } else {
        navigate(`/p2p/trade/${tradeId}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trade failed");
    }
    setSubmitting(false);
  };

  const country = status?.country || "—";
  const fiatLabel = country === "india" ? "INR" : country === "pakistan" ? "PKR" : country === "nigeria" ? "NGN" : "";
  const needsKyc = (country === "india" || country === "nigeria");

  // Calculate USDT amount in real-time
  const usdtPreview = selectedAd && fiatAmount ? (parseFloat(fiatAmount) / selectedAd.price_per_usdt).toFixed(2) : "0.00";

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1" data-testid="p2p-back-btn">
            <ChevronLeft size={24} />
          </button>
          <div className="flex items-center gap-2">
            <Users size={20} className="text-primary" />
            <h1 className="text-lg font-bold">P2P Trading</h1>
          </div>
          <button onClick={() => navigate("/p2p/my-trades")} className="text-xs text-primary" data-testid="p2p-my-trades-link">
            <ListChecks size={20} />
          </button>
        </div>

        {/* Region & KYC badge */}
        <div className="px-4 pb-3 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            Region: <span className="text-primary font-semibold uppercase">{country}</span> • Routing:{" "}
            <span className="text-primary font-semibold">{sourceParam === "outside" ? "USDT" : "Game Wallet"}</span>
          </span>
          {status && needsKyc && (
            status.kyc_verified ? (
              <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 flex items-center gap-1">
                <ShieldCheck size={10} /> KYC Verified
              </span>
            ) : (
              <button
                onClick={() => navigate("/p2p/kyc")}
                className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 flex items-center gap-1 transition"
                data-testid="kyc-pending-cta"
              >
                <AlertCircle size={10} /> KYC Pending — Verify
              </button>
            )
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-t border-border">
          <button className="flex-1 py-3 text-sm font-semibold text-primary border-b-2 border-primary" data-testid="p2p-tab-buy">
            <ShoppingBag size={16} className="inline mr-1" /> Buy USDT
          </button>
          <button
            onClick={() => navigate(`/p2p/post?source=${sourceParam}`)}
            className="flex-1 py-3 text-sm font-semibold text-muted-foreground"
            data-testid="p2p-tab-sell"
          >
            <Plus size={16} className="inline mr-1" /> Sell USDT
          </button>
        </div>
      </div>

      {/* Pre-verify CTA banner (KYC-required countries + not verified) */}
      {status && needsKyc && !status.kyc_verified && (
        <div className="mx-4 mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 flex items-start gap-2">
          <ShieldCheck size={18} className="text-yellow-400 shrink-0 mt-0.5" />
          <div className="text-xs flex-1">
            <div className="font-semibold text-yellow-300 mb-1">Complete KYC first</div>
            <div className="text-muted-foreground">
              {country === "nigeria"
                ? "Verify your BVN + NIN + Selfie once. Future trades reveal seller's payment details instantly."
                : "Verify your Aadhar + Selfie once. Future trades will get the seller's bank account instantly — no waiting required."}
            </div>
          </div>
          <button
            onClick={() => navigate("/p2p/kyc")}
            className="text-xs bg-yellow-500 text-black px-3 py-1.5 rounded-lg font-semibold shrink-0"
            data-testid="kyc-banner-cta"
          >
            Verify Now
          </button>
        </div>
      )}

      {/* Ad list */}
      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            <Users size={48} className="mx-auto mb-3 opacity-40" />
            No ads available in your region.<br />Be the first to post an ad!
          </div>
        ) : (
          ads.map((ad) => (
            <div key={ad.id} className="bg-card border border-border rounded-xl p-4 space-y-2" data-testid={`p2p-ad-card-${ad.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono font-semibold text-sm text-primary">{ad.seller_code_name || "Trader"}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Star size={10} className="text-yellow-400" />
                    {ad.seller_reputation?.total_trades || 0} trades •{" "}
                    {ad.seller_reputation?.completion_rate || 0}% completion
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-primary">{ad.price_per_usdt}</div>
                  <div className="text-[10px] text-muted-foreground">{ad.fiat}/USDT</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Available: <span className="text-foreground font-semibold">{ad.available_amount_usdt.toFixed(2)} USDT</span> • Limits:{" "}
                <span className="text-foreground">{ad.min_limit_fiat}–{ad.max_limit_fiat} {ad.fiat}</span>
              </div>
              <div className="text-[10px] text-muted-foreground">
                Payment: {(ad.payment_methods || []).map((p) => p.method?.replace("_", " ")).join(", ") || "Bank Transfer"}
              </div>
              {ad.notes && <div className="text-xs italic text-muted-foreground">"{ad.notes}"</div>}
              <button
                onClick={() => openBuyModal(ad)}
                className="w-full py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition"
                data-testid={`p2p-buy-btn-${ad.id}`}
              >
                Buy USDT
              </button>
            </div>
          ))
        )}
      </div>

      {/* Floating my-ads link */}
      <button
        onClick={() => navigate("/p2p/my-ads")}
        className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg text-xs font-semibold"
        data-testid="p2p-my-ads-link"
      >
        My Ads
      </button>

      {/* Buy Order Modal */}
      {selectedAd && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4" onClick={closeBuyModal}>
          <div
            className="bg-card border border-border rounded-2xl w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="buy-order-modal"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Buy USDT from <span className="font-mono text-primary">{selectedAd.seller_code_name}</span></h2>
              <button onClick={closeBuyModal} className="p-1" data-testid="close-buy-modal"><X size={20} /></button>
            </div>

            <div className="bg-background rounded-lg p-3 space-y-1.5 text-xs">
              <Row label="Price" value={`${selectedAd.price_per_usdt} ${selectedAd.fiat}/USDT`} />
              <Row label="Available" value={`${selectedAd.available_amount_usdt.toFixed(2)} USDT`} />
              <Row label="Limits" value={`${selectedAd.min_limit_fiat} – ${selectedAd.max_limit_fiat} ${selectedAd.fiat}`} />
              <Row label="Payment" value={(selectedAd.payment_methods || []).map((p) => p.method?.replace("_", " ")).join(", ")} />
              <Row label="USDT credited to" value={sourceParam === "outside" ? "Total Deposited (auto-stake 1% daily)" : "Game Wallet"} />
            </div>

            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">
                I want to pay ({selectedAd.fiat})
              </label>
              <input
                type="number"
                value={fiatAmount}
                onChange={(e) => setFiatAmount(e.target.value)}
                placeholder={`${selectedAd.min_limit_fiat} – ${selectedAd.max_limit_fiat}`}
                className="w-full bg-background border border-border rounded-lg px-3 py-3 text-lg font-bold"
                data-testid="buy-fiat-input"
                autoFocus
              />
              <div className="flex gap-1.5 flex-wrap">
                {[selectedAd.min_limit_fiat, Math.floor((selectedAd.min_limit_fiat + selectedAd.max_limit_fiat) / 2), selectedAd.max_limit_fiat]
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .map((v) => (
                    <button
                      key={v}
                      onClick={() => setFiatAmount(String(v))}
                      className="text-[10px] px-2 py-1 bg-background border border-border rounded"
                    >
                      {v}
                    </button>
                  ))}
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 text-center">
              <div className="text-xs text-muted-foreground">You will receive</div>
              <div className="text-2xl font-bold text-primary" data-testid="usdt-preview">
                {usdtPreview} USDT
              </div>
            </div>

            {needsKyc && !status?.kyc_verified && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-[11px] text-yellow-300">
                ⚠️ After clicking Buy, KYC verification will be required before seller's {country === "nigeria" ? "payment account" : "bank"} is revealed.
              </div>
            )}

            <button
              onClick={confirmBuy}
              disabled={submitting || !fiatAmount}
              className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-bold disabled:opacity-50"
              data-testid="confirm-buy-btn"
            >
              {submitting ? <Loader2 className="animate-spin inline" /> : `Place Order — Buy ${usdtPreview} USDT`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

export default P2PMarketplacePage;
